import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { LangueParlee } from '@sirocco/shared';
import { SiroccoApiError } from '../../api/client';
import { BrandScreen } from '../../components/layout/Screens';
import { Button } from '../../components/ui/Button';
import { ChoixDesLangues } from '../../components/ui/ChoixDesLangues';
import { Checkbox, Field } from '../../components/ui/Field';
import { useSession } from '../../store/SessionContext';
import { SiroccoLogo } from '../../theme/illustrations';
import { maskFrenchDate, parseFrenchDate } from '../../utils/format';
import { fetchLegalConfig, recordConsent, type ConfigLegale } from '../../firebase/legal';
import { LienLegal } from '../legal/LienLegal';
import { auth as authFirebase } from '../../firebase/app';
import { langue, t } from '../../i18n';

/**
 * Ecran d'inscription.
 *
 * Les trois regles de mot de passe s'allument au fur et a mesure de la
 * saisie ; le serveur les revalide de son cote.
 */
export function RegisterScreen() {
  const { register } = useSession();
  const navigate = useNavigate();

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [birthDate, setBirthDate] = useState('');
  /* La langue du telephone est deja cochee : on ne fait qu'ajouter les autres. */
  const [languesParlees, setLanguesParlees] = useState<LangueParlee[]>([langue]);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [field, setField] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  /*
    Les documents a accepter, avec leur version. Charges des l'ouverture
    de l'ecran : sans eux on ne peut ni afficher les liens, ni consigner
    ce qui a ete accepte. Leur absence n'empeche pas de s'inscrire — on
    n'enferme personne dehors — mais elle prive la preuve de sa version.
  */
  const [legal, setLegal] = useState<ConfigLegale | null>(null);
  useEffect(() => {
    void fetchLegalConfig().then(setLegal);
  }, []);

  const rules = useMemo(
    () => [
      { label: t.auth.inscription.regles.huit, ok: password.length >= 8 },
      { label: t.auth.inscription.regles.casse, ok: /[a-z]/.test(password) && /[A-Z]/.test(password) },
      {
        label: t.auth.inscription.regles.chiffre,
        ok: /[0-9]/.test(password) || /[^A-Za-z0-9]/.test(password),
      },
    ],
    [password],
  );

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setField(null);

    /*
      Tous les champs comptent. Firebase refusait bien une adresse ou un
      mot de passe vide, mais un prenom, un nom ou un pseudo vides
      passaient — et un pseudo vide aurait ecrit une cle vide dans l'index
      d'unicite. On le dit champ par champ plutot qu'en bloc : « remplis
      tout » n'aide personne a trouver ce qui manque.
    */
    const manquant = (
      [
        ['firstName', firstName, t.auth.inscription.manque.prenom],
        ['lastName', lastName, t.auth.inscription.manque.nom],
        ['username', username, t.auth.inscription.manque.pseudo],
        ['email', email, t.auth.inscription.manque.email],
        ['password', password, t.auth.inscription.manque.motDePasse],
        ['birthDate', birthDate, t.auth.inscription.manque.naissance],
      ] as const
    ).find(([, valeur]) => valeur.trim().length === 0);

    if (manquant) {
      setError(manquant[2]);
      setField(manquant[0]);
      return;
    }

    const isoBirthDate = parseFrenchDate(birthDate);
    if (!isoBirthDate) {
      setError(t.auth.inscription.naissanceInvalide);
      setField('birthDate');
      return;
    }
    if (!acceptTerms) {
      setError(t.auth.inscription.conditions);
      setField('acceptTerms');
      return;
    }

    setPending(true);
    try {
      await register({
        firstName,
        lastName,
        username,
        email,
        password,
        birthDate: isoBirthDate,
        acceptTerms: true,
        languesParlees,
      });

      /*
        La preuve, ecrite juste apres la creation du compte : avant, il
        n'y a pas d'identifiant sous lequel la ranger. Son echec ne
        retient pas l'inscription — elle est gardee de cote et rejouee.
      */
      const uid = authFirebase().currentUser?.uid;
      if (uid && legal) await recordConsent(uid, legal);
      /*
        On propose d'abord une photo et deux lignes, puis vient le choix
        du serveur. Dans cet ordre : le premier ecran d'un nouveau membre
        est une liste de gens, et on s'y presente mieux avec un visage.
      */
      navigate('/personalize', { replace: true });
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : t.auth.inscription.impossible);
      if (cause instanceof SiroccoApiError && cause.field) setField(cause.field);
    } finally {
      setPending(false);
    }
  }

  return (
    <BrandScreen>
      <div className="auth auth--register">
        <div className="auth__logo">
          <SiroccoLogo markSize={50} />
        </div>

        <h1 className="auth__title">{t.auth.inscription.titre}</h1>
        <p className="auth__switch auth__switch--top">
          {t.auth.inscription.dejaInscrit} <Link to="/login">{t.auth.inscription.seConnecter}</Link>
        </p>

        <form className="auth__form" onSubmit={onSubmit} noValidate>
          <div className="auth__row">
            <Field
              label={t.auth.inscription.prenom}
              placeholder={t.auth.inscription.prenom}
              autoComplete="given-name"
              value={firstName}
              onChange={(event) => setFirstName(event.target.value)}
              error={field === 'firstName' ? error : null}
            />
            <Field
              label={t.auth.inscription.nom}
              placeholder={t.auth.inscription.nom}
              autoComplete="family-name"
              value={lastName}
              onChange={(event) => setLastName(event.target.value)}
              error={field === 'lastName' ? error : null}
            />
          </div>

          <Field
            label={t.auth.inscription.pseudo}
            placeholder={t.auth.inscription.pseudoExemple}
            autoComplete="username"
            value={username}
            onChange={(event) => setUsername(event.target.value)}
            error={field === 'username' ? error : null}
            className="auth__block"
          />

          <Field
            label={t.auth.champs.email}
            type="email"
            inputMode="email"
            placeholder={t.auth.champs.exempleEmail}
            autoComplete="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            error={field === 'email' ? error : null}
            className="auth__block"
          />

          <Field
            label={t.auth.champs.motDePasse}
            type="password"
            placeholder="••••••••"
            autoComplete="new-password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            error={field === 'password' ? error : null}
            className="auth__block"
          />

          <ul className="auth__rules">
            {rules.map((rule) => (
              <li key={rule.label} className={rule.ok ? 'auth__rule auth__rule--ok' : 'auth__rule'}>
                <span className="auth__bullet" />
                {rule.label}
              </li>
            ))}
          </ul>

          <Field
            label={t.auth.inscription.naissance}
            inputMode="numeric"
            placeholder={t.temps.masqueDate}
            value={birthDate}
            onChange={(event) => setBirthDate(maskFrenchDate(event.target.value))}
            error={field === 'birthDate' ? error : null}
            className="auth__block"
          />

          {/*
            Les langues qu'on parle, pour que les sorties ou l'on se
            comprend passent devant. On ne scinde pas une ville par langue :
            c'est a la sortie que la langue compte.
          */}
          <ChoixDesLangues
            tone="brand"
            className="auth__block"
            label={t.auth.inscription.langues}
            aide={t.auth.inscription.languesAide}
            valeur={languesParlees}
            onChange={setLanguesParlees}
          />

          {/*
            La case n'est jamais pre-cochee : un consentement pre-donne
            n'en est pas un. Les deux documents s'ouvrent d'ici, dans le
            navigateur interne, sans perdre le formulaire en cours.
          */}
          <div className="auth__terms">
            <Checkbox
              checked={acceptTerms}
              onChange={setAcceptTerms}
              label={t.auth.inscription.accepteLabel}
            />
            <p>
              {t.auth.inscription.accepte.debut}{' '}
              <LienLegal url={legal?.cgu.url ?? 'https://siroccoapp.fr/cgu'}>
                {t.auth.inscription.accepte.cgu}
              </LienLegal>{' '}
              {t.auth.inscription.accepte.et}{' '}
              <LienLegal url={legal?.privacy.url ?? 'https://siroccoapp.fr/confidentialite'}>
                {t.auth.inscription.accepte.confidentialite}
              </LienLegal>{' '}
              {t.auth.inscription.accepte.fin}
            </p>
          </div>
          {field === 'acceptTerms' && error && <p className="auth__error">{error}</p>}

          {error && !field && <p className="auth__error">{error}</p>}

          <Button type="submit" variant="brand" block loading={pending} className="auth__submit">
            {t.auth.inscription.jeMInscris}
          </Button>
        </form>
      </div>
    </BrandScreen>
  );
}
